/**
 * User profile dropdown component for the header
 */
import React from 'react';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CrownOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  FmAvatar,
  FmButton,
  FmText,
  FmInline,
  FmMenu,
  FmStack,
  type FmMenuItem,
} from '@/components/ui';
import { useLanguage } from '@/i18n';

/** Account destinations the menu offers; `null` when the owner is elsewhere. */
export type UserProfileDestination = 'settings' | 'pricing' | 'about';

interface UserProfileProps {
  /** Localized catalog labels of the account utility routes. */
  settingsLabel: string;
  aboutLabel: string;
  /** The account destination the owner is on, marked as the current page. */
  currentDestination: UserProfileDestination | null;
  onViewSettings: () => void;
  onViewPricing: () => void;
  onViewAbout: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({
  settingsLabel,
  aboutLabel,
  currentDestination,
  onViewSettings,
  onViewPricing,
  onViewAbout,
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();

  if (!isAuthenticated || !user) {
    return null;
  }

  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username;
  };

  const getUserTypeIcon = () => {
    switch (user.user_type) {
      case 'admin':
        return <CrownOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const getUserTypeBadge = () => {
    if (!t.auth?.userRoles) return null;

    const roleMap: { [key: string]: keyof typeof t.auth.userRoles } = {
      admin: 'administrator',
      standard: 'standard',
      viewer: 'viewer',
    };
    const roleKey = roleMap[user.user_type] || 'standard';
    const roleText = t.auth.userRoles[roleKey];

    return (
      <FmText as="span" size="fine" tone={user.user_type === 'viewer' ? 'positive' : 'accent'}>
        {roleText}
      </FmText>
    );
  };

  const menuItems: FmMenuItem[] = [
    {
      key: 'user-info',
      type: 'label',
      label: (
        <FmStack gap="xs">
          <FmText weight="medium">{getUserDisplayName()}</FmText>
          <FmText size="fine" tone="subtle">
            {user.email}
          </FmText>
          {getUserTypeBadge()}
          {user.organization && (
            <FmText size="tiny" tone="muted">
              {user.organization}
            </FmText>
          )}
        </FmStack>
      ),
    },
    {
      key: 'profile-separator',
      type: 'separator',
    },
    {
      key: 'profile',
      current: currentDestination === 'settings',
      label: settingsLabel,
      icon: <SettingOutlined />,
      onClick: onViewSettings,
      'data-testid': 'menu-item-profile',
    },
    {
      key: 'pricing',
      current: currentDestination === 'pricing',
      label: t.shell.nav.subscriptionAndPricing,
      icon: <CreditCardOutlined />,
      onClick: onViewPricing,
      'data-testid': 'menu-item-pricing',
    },
    {
      key: 'about',
      current: currentDestination === 'about',
      label: aboutLabel,
      icon: <InfoCircleOutlined />,
      onClick: onViewAbout,
      'data-testid': 'menu-item-about',
    },
    {
      key: 'logout-separator',
      type: 'separator',
    },
    {
      key: 'logout',
      label: t?.auth?.signOut,
      icon: <LogoutOutlined />,
      onClick: logout,
      'data-testid': 'menu-item-logout',
    },
  ];

  return (
    <FmMenu
      items={menuItems}
      trigger="hover"
      placement="bottomRight"
      menuTestId="user-profile-menu"
      minWidth="var(--fm-menu-min-w)"
    >
      <FmButton
        variant="text"
        data-testid="user-profile-trigger"
        aria-label={getUserDisplayName()}
        aria-current={currentDestination ? 'page' : undefined}
      >
        <FmInline align="center" wrap={false}>
          <FmAvatar
            size="sm"
            tone={user.user_type === 'admin' ? 'accent' : 'neutral'}
            icon={getUserTypeIcon()}
          />
          <FmText as="span" truncate>
            {getUserDisplayName()}
          </FmText>
        </FmInline>
      </FmButton>
    </FmMenu>
  );
};
